import { api, formatError } from '../api-client'
import { analyticsEvent, flushAndExit } from '../analytics'
import { parseArgs } from './context/args'
import { emitJSON } from './context/render'
import { levenshtein } from './context/flags'
import { resolveWorkspaceSlug } from './context'

// Repo CLI — link/unlink the GitHub repos a workspace tracks. Reads + writes are
// direct API calls; the dashboard renders the same list. Linking a repo is what
// lets the GitHub App attribute pushes/PRs to this workspace.

const KNOWN_REPO_FLAGS: ReadonlySet<string> = new Set(['json', 'help'])

export function assertKnownRepoFlags(
  flags: Record<string, string | boolean>,
  onError: (msg: string) => never = (msg) => { console.error(msg); process.exit(1) },
): void {
  const unknown = Object.keys(flags).filter((f) => !KNOWN_REPO_FLAGS.has(f))
  if (unknown.length === 0) return
  const lines = unknown.map((f) => {
    let best: string | null = null
    let bestDist = Infinity
    for (const known of KNOWN_REPO_FLAGS) {
      const d = levenshtein(f, known)
      if (d < bestDist) { bestDist = d; best = known }
    }
    const hint = best !== null && bestDist <= Math.max(2, Math.floor(f.length / 2)) ? best : null
    return `  --${f}${hint ? `  (did you mean --${hint}?)` : ''}`
  })
  onError(
    `Error: unknown flag${unknown.length > 1 ? 's' : ''} for 'driftless repo':\n` +
      lines.join('\n') +
      `\nRun 'driftless repo --help' for valid flags.`,
  )
}

// Help lives in ./help-text (light, dep-free) so index.ts can print it without
// importing this command body; re-exported here so REPO_HELP still resolves from './repo'.
export { REPO_HELP } from './help-text'
import { REPO_HELP } from './help-text'

function printHelp(stream: NodeJS.WritableStream = process.stdout): void {
  stream.write(REPO_HELP)
}

export async function repoCommand(args: string[]): Promise<void> {
  const { flags, positional } = parseArgs(args)
  const sub = positional[0]
  const isJSON = !!flags['json']

  if (flags['help'] === true || sub === 'help') { printHelp(); process.exit(0) }
  if (sub === undefined) { printHelp(process.stderr); process.exit(1) }
  assertKnownRepoFlags(flags)

  const ws = await resolveWorkspaceSlug()
  const base = `/workspaces/${ws}/repos`

  try {
    switch (sub) {
      case 'list': {
        const result = (await api.get(base)) as any[]
        if (isJSON) { emitJSON(result); break }
        if (!Array.isArray(result) || !result.length) { console.log('No repos linked. Link one with: driftless repo link <org/repo>'); break }
        for (const r of result) {
          const full = r.full_name ?? `${r.github_org ?? '?'}/${r.github_repo ?? '?'}`
          console.log(`${r.id}  ${full}`)
        }
        break
      }
      case 'link': {
        const nwo = positional[1]
        if (!nwo) { console.error('Error: a repo is required. Usage: driftless repo link <org/repo>'); process.exit(1) }
        const slash = nwo.indexOf('/')
        if (slash <= 0 || slash === nwo.length - 1) {
          console.error('Error: repo must be in "org/repo" form (e.g. acme/billing).'); process.exit(1)
        }
        const github_org = nwo.slice(0, slash).trim()
        const github_repo = nwo.slice(slash + 1).trim()
        const result = (await api.post(base, { github_org, github_repo })) as any
        analyticsEvent('repo_linked', ws, {})
        if (isJSON) emitJSON(result)
        else console.log(`Repo linked — ${github_org}/${github_repo} (id ${result?.id ?? '?'})`)
        break
      }
      case 'unlink': {
        const id = positional[1]
        if (!id) { console.error('Error: repo id required. Usage: driftless repo unlink <id>'); process.exit(1) }
        const result = (await api.delete(`${base}/${encodeURIComponent(id)}`)) as any
        analyticsEvent('repo_unlinked', ws, {})
        if (isJSON) emitJSON(result ?? { deleted: id })
        else console.log(`Repo unlinked — id ${result?.deleted ?? id}`)
        break
      }
      default:
        console.error(`Unknown repo subcommand: '${sub}'. Run 'driftless repo --help'.`)
        process.exit(1)
    }
    await flushAndExit(0)
  } catch (e: any) {
    console.error(formatError(e))
    await flushAndExit(1)
  }
}
